import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useBranch } from '../contexts/BranchContext';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Calendar, User, Clock, TrendUp, FileText, Bed, MagnifyingGlass, PencilSimple, Activity } from '@phosphor-icons/react';
import api from '../utils/api';
import { toast } from 'sonner';
import CustomerDetailModal from '../components/CustomerDetailModal';
import EditTrackingModal from '../components/EditTrackingModal';
import BMIDisplay from '../components/BMIDisplay';

const Tracking = () => {
  const { activeBranch } = useBranch();
  const [trackingData, setTrackingData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [staffStats, setStaffStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [showCustomerDetail, setShowCustomerDetail] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [filters, setFilters] = useState({
    searchQuery: '',
    dateFilter: new Date().toISOString().split('T')[0], // Auto-populate today's date
    staffFilter: ''
  });

  const fetchTrackingData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/tracking/all-sessions', {
        params: {
          branch_id: activeBranch?.branch_id,
          date: filters.dateFilter  // Pass selected date to backend
        }
      });
      const sessions = response.data.sessions || [];
      setTrackingData(sessions);
      setFilteredData(sessions);
      
      // Calculate staff stats from the initial data
      recalculateStaffStats(sessions);
    } catch (error) {
      toast.error('Failed to load tracking data');
    } finally {
      setLoading(false);
    }
  }, [activeBranch, filters.dateFilter]);

  const applyFilters = useCallback(() => {
    let filtered = [...trackingData];

    // Search filter
    if (filters.searchQuery) {
      filtered = filtered.filter(session =>
        session.customer_name?.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
        session.customer_reg_no?.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
        session.created_by_name?.toLowerCase().includes(filters.searchQuery.toLowerCase())
      );
    }

    // NOTE: Date filter is handled by backend API, not here
    // Backend already filters by date when fetching data
    // Applying it again here causes double-filtering and "No records" issue

    // Staff filter
    if (filters.staffFilter) {
      filtered = filtered.filter(session => 
        session.created_by_name?.toLowerCase().includes(filters.staffFilter.toLowerCase())
      );
    }

    setFilteredData(filtered);
    
    // Recalculate staff statistics based on filtered data
    recalculateStaffStats(filtered);
  }, [trackingData, filters]);

  useEffect(() => {
    if (activeBranch) {
      fetchTrackingData();
    }
  }, [activeBranch, fetchTrackingData]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const recalculateStaffStats = (sessions) => {
    const staffMap = {};
    
    // Only count sessions where customer was assigned a bed
    const sessionsWithBeds = sessions.filter(session => session.bed_number);
    
    sessionsWithBeds.forEach(session => {
      const staffName = session.created_by_name;
      const bedNumber = session.bed_number;
      const batchType = session.batch_type;
      
      if (!staffMap[staffName]) {
        staffMap[staffName] = {
          name: staffName,
          total_sessions: 0,
          regular_beds: new Set(),
          premium_beds: new Set()
        };
      }
      
      staffMap[staffName].total_sessions += 1;
      
      if (bedNumber && batchType) {
        if (batchType === 'premium') {
          staffMap[staffName].premium_beds.add(bedNumber);
        } else {
          staffMap[staffName].regular_beds.add(bedNumber);
        }
      }
    });
    
    // Convert to array with counts
    const stats = Object.values(staffMap).map(staff => ({
      name: staff.name,
      total_sessions: staff.total_sessions,
      regular_beds_attended: staff.regular_beds.size,
      premium_beds_attended: staff.premium_beds.size,
      unique_beds_attended: staff.regular_beds.size + staff.premium_beds.size
    }));
    
    setStaffStats(stats);
  };

  const handleResetFilters = () => {
    setFilters({
      searchQuery: '',
      dateFilter: '',
      staffFilter: ''
    });
    // Reset to show all data
    setFilteredData(trackingData);
    recalculateStaffStats(trackingData);
  };

  const handleCustomerClick = (customerId) => {
    setSelectedCustomerId(customerId);
    setShowCustomerDetail(true);
  };

  const handleEditClick = (session) => {
    setSelectedSession(session);
    setShowEditModal(true);
  };

  const handleEditUpdate = () => {
    fetchTrackingData(); // Refresh data after update
    setShowEditModal(false);
    setSelectedSession(null);
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-4 sm:p-6 lg:p-8">
          <div className="text-center py-12">
            <p className="text-lg text-[#1C1C1A]/60">Loading tracking data...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-[#2C4C3B] mb-2">Client Tracking</h1>
          <p className="text-[#1C1C1A]/60">
            View all session notes and staff attendance records
          </p>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Search Customer</Label>
              <div className="relative">
                <Input
                  value={filters.searchQuery}
                  onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value })}
                  placeholder="Name or Reg No..."
                  className="pl-10"
                />
                <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={filters.dateFilter}
                onChange={(e) => setFilters({ ...filters, dateFilter: e.target.value })}
              />
            </div>

            <div>
              <Label>Staff Member</Label>
              <Input
                value={filters.staffFilter}
                onChange={(e) => setFilters({ ...filters, staffFilter: e.target.value })}
                placeholder="Staff name..."
              />
            </div>

            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={handleResetFilters}
                className="w-full"
              >
                Reset Filters
              </Button>
            </div>
          </div>
        </Card>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100">
            <div className="flex items-center gap-3">
              <FileText size={28} className="text-blue-600 flex-shrink-0" weight="fill" />
              <div>
                <p className="text-xs sm:text-sm text-blue-600">Total Sessions</p>
                <p className="text-xl sm:text-2xl font-bold text-blue-900">{filteredData.length}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100">
            <div className="flex items-center gap-3">
              <User size={28} className="text-green-600 flex-shrink-0" weight="fill" />
              <div>
                <p className="text-xs sm:text-sm text-green-600">Unique Customers</p>
                <p className="text-xl sm:text-2xl font-bold text-green-900">
                  {new Set(filteredData.map(d => d.customer_id)).size}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100">
            <div className="flex items-center gap-3">
              <TrendUp size={28} className="text-purple-600 flex-shrink-0" weight="fill" />
              <div>
                <p className="text-xs sm:text-sm text-purple-600">Avg Effectiveness</p>
                <p className="text-xl sm:text-2xl font-bold text-purple-900">
                  {filteredData.length > 0
                    ? Math.round(
                        filteredData.reduce((sum, d) => sum + d.treatment_effectiveness, 0) /
                          filteredData.length
                      )
                    : 0}%
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Staff Bed Attendance Statistics */}
        {staffStats.length > 0 && (
          <Card className="p-6 mb-6">
            <h3 className="text-lg font-semibold text-[#2C4C3B] mb-4 flex items-center gap-2">
              <User size={24} weight="fill" />
              Staff Bed Attendance
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {staffStats.map((staff, idx) => (
                <Card key={idx} className="p-4 bg-gradient-to-br from-orange-50 to-orange-100">
                  <p className="font-medium text-[#2C4C3B] mb-3 truncate" title={staff.name}>{staff.name}</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#1C1C1A]/60">Total Sessions:</span>
                      <span className="font-semibold text-orange-900">{staff.total_sessions}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#1C1C1A]/60">Regular Beds:</span>
                      <Badge className="bg-blue-600 text-white">
                        {staff.regular_beds_attended || 0}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#1C1C1A]/60">Premium Beds:</span>
                      <Badge className="bg-amber-600 text-white">
                        {staff.premium_beds_attended || 0}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between text-sm pt-1 border-t border-orange-200">
                      <span className="text-[#1C1C1A]/70 font-medium">Total Beds:</span>
                      <Badge className="bg-orange-700 text-white">
                        {staff.unique_beds_attended || 0}
                      </Badge>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </Card>
        )}

        {/* Tracking Records */}
        <div className="space-y-4">
          {filteredData.length === 0 ? (
            <Card className="p-12 text-center">
              <FileText size={48} className="mx-auto mb-4 text-gray-400" />
              <p className="text-lg text-[#1C1C1A]/60">No tracking records found</p>
              <p className="text-sm text-[#1C1C1A]/40 mt-2">
                Try adjusting your filters or check in customers to start tracking
              </p>
            </Card>
          ) : (
            filteredData.map((record) => (
              <Card key={record.note_id} className="p-4 hover:shadow-lg transition-shadow">
                <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                  {/* Left: Customer & Date Info */}
                  <div 
                    className="flex-shrink-0 lg:w-64 cursor-pointer hover:bg-green-50 p-2 rounded-lg transition-all"
                    onClick={() => handleCustomerClick(record.customer_id)}
                    title="Click to view complete customer profile"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <User size={24} className="text-[#2C4C3B]" weight="fill" />
                      <div>
                        <h3 className="font-semibold text-[#2C4C3B] hover:underline">{record.customer_name}</h3>
                        <p className="text-xs text-[#1C1C1A]/60">Reg: {record.customer_reg_no}</p>
                        <p className="text-[10px] text-blue-600 font-medium mt-1">Click for full profile →</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-[#1C1C1A]/70 mb-1">
                      <Calendar size={16} />
                      <span>{record.session_date}</span>
                    </div>

                    {record.batch_time && (
                      <div className="flex items-center gap-2 text-sm text-[#1C1C1A]/70 mb-1">
                        <Clock size={16} />
                        <span>Batch: {record.batch_time}</span>
                      </div>
                    )}

                    {record.bed_number && (
                      <div className="flex items-center gap-2 text-sm text-[#1C1C1A]/70">
                        <Bed size={16} />
                        <span>Bed #{record.bed_number}</span>
                      </div>
                    )}
                  </div>

                  {/* Middle: Session Details */}
                    <div className="flex-1">
                    {record.note_type === 'counselling' && (
                      <div className="flex items-center gap-2 mb-3">
                        <Badge data-testid="counselling-note-badge" className="bg-rose-100 text-rose-800">Counselling Session</Badge>
                      </div>
                    )}
                    {record.note_type !== 'BMI' && record.note_type !== 'counselling' && (
                      <div className="flex items-center gap-2 mb-3">
                        <Badge className="bg-green-100 text-green-800">
                          {record.treatment_effectiveness}% Effective
                        </Badge>
                      </div>
                    )}

                    <p className="text-sm text-[#1C1C1A] mb-3">{record.session_notes}</p>

                    {/* BMI Data Display */}
                    {record.note_type === 'BMI' && record.bmi_data && (
                      <BMIDisplay bmiData={record.bmi_data} />
                    )}

                    {/* Improvement Notes */}
                    {record.improvement_action_notes && (
                      <div className="mb-2 p-2 bg-blue-50 rounded text-xs">
                        <span className="font-medium text-blue-800">Action: </span>
                        <span className="text-[#1C1C1A]/80">{record.improvement_action_notes}</span>
                      </div>
                    )}

                    {record.improvement_result_notes && (
                      <div className="mb-2 p-2 bg-green-50 rounded text-xs">
                        <span className="font-medium text-green-800">Result: </span>
                        <span className="text-[#1C1C1A]/80">{record.improvement_result_notes}</span>
                      </div>
                    )}

                    {/* Therapy List */}
                    {record.therapy_list && record.therapy_list.length > 0 && (
                      <div className="mb-2">
                        <p className="text-xs text-[#1C1C1A]/60 mb-1">Therapies:</p>
                        <div className="flex flex-wrap gap-1">
                          {record.therapy_list.map((therapy, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs bg-purple-50">
                              {therapy}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Report Image */}
                    {record.report_image && (
                      <div className="mt-2">
                        <img
                          src={record.report_image.startsWith('data:') ? record.report_image : `${process.env.REACT_APP_BACKEND_URL}/api/storage/files/${record.report_image}`}
                          alt="Report"
                          className="max-h-24 rounded border cursor-pointer hover:opacity-80"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Create a new window with the image
                            const win = window.open();
                            const imageUrl = record.report_image.startsWith('data:') ? record.report_image : `${process.env.REACT_APP_BACKEND_URL}/api/storage/files/${record.report_image}`;
                            win.document.write(`
                              <html>
                                <head>
                                  <title>Report Image</title>
                                  <style>
                                    body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #000; }
                                    img { max-width: 100%; max-height: 100vh; object-fit: contain; }
                                  </style>
                                </head>
                                <body>
                                  <img src="${imageUrl}" alt="Report Image" />
                                </body>
                              </html>
                            `);
                            win.document.close();
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Right: Staff Info & Edit Button */}
                  <div className="flex-shrink-0 lg:w-48">
                    <div className="p-3 bg-gray-50 rounded-lg mb-3">
                      <p className="text-xs text-[#1C1C1A]/60 mb-1">{record.note_type === 'counselling' ? 'Counselled by:' : 'Attended by:'}</p>
                      <p className="font-semibold text-[#2C4C3B]">{record.created_by_name}</p>
                      <p className="text-xs text-[#1C1C1A]/50 mt-2">
                        {new Date(record.created_at).toLocaleString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    
                    {/* Edit Button (counselling notes are managed in the Counselling page) */}
                    {record.note_type !== 'counselling' && (
                    <Button
                      onClick={() => handleEditClick(record)}
                      className="w-full bg-[#2C4C3B] hover:bg-[#1F3529] text-white"
                      size="sm"
                    >
                      <PencilSimple size={16} className="mr-2" weight="bold" />
                      Edit Notes
                    </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        {/* Customer Detail Modal */}
        <CustomerDetailModal
          open={showCustomerDetail}
          onClose={() => {
            setShowCustomerDetail(false);
            setSelectedCustomerId(null);
          }}
          customerId={selectedCustomerId}
        />

        {/* Edit Tracking Modal */}
        <EditTrackingModal
          open={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedSession(null);
          }}
          sessionData={selectedSession}
          onUpdate={handleEditUpdate}
        />
      </div>
    </Layout>
  );
};

export default Tracking;
